import React, { useState, useEffect, useCallback } from "react";
import CustomHeader from "../../components/header/index.jsx";
import url from "../../config.js";
import "./tenantadmin.scss";

const domain = url.domain;
const backendUrl = url.backendUrl;

// Maps a role's permission keys → the page/feature each one unlocks. Ordered so the
// guide reads top-down (navigation → Smart Hub → agents → techs → admin). This is the
// single source of truth for the Role guide; it renders straight from each role's
// live `permissions`, so it never drifts from what's seeded in the DB.
// designAgent/integrationAgent are omitted — they're default permissions and not
// worth surfacing. `smartHub` isn't listed directly; it's expanded into the named
// tabs (New Integrations + Enhance Existing) in pagesFor().
const PERMISSION_PAGE_MAP = [
  { key: "projectCenter",    label: "Project Center" },
  { key: "connectedApps",    label: "Connected Apps" },
  { key: "greenfieldAgent",  label: "Smart Hub → New Integrations" },
  { key: "brownfieldAgent",  label: "Smart Hub → Enhance Existing" },
  { key: "migrationAgent",   label: "Smart Hub → Migration" },
  { key: "migrationDotnet",  label: "Migration: MuleSoft → .NET" },
  { key: "migrationJitterbit", label: "Migration: Jitterbit → MuleSoft" },
  { key: "upgradeAgent",     label: "Upgrade Agent" },
  { key: "techMule",         label: "New Integrations: Mule" },
  { key: "techSpringBoot",   label: "New Integrations: Spring Boot" },
  { key: "techDotNet",       label: "New Integrations: .NET" },
  { key: "tenantAdmin",      label: "Tenant Admin" },
  { key: "superAdmin",       label: "Super Admin" },
];

// Base roles that only grant default access — hidden from the guide.
const ROLE_GUIDE_HIDE = ["user", "moderator"];

// Collapsible reference: for each role, list the pages/features it unlocks, derived
// from the role's own permissions. `smartHub` is the umbrella that already grants both
// Smart Hub tabs, so when it's set we hide the redundant per-tab labels.
function RoleGuide({ roles }) {
  const [open, setOpen] = useState(false);
  const visibleRoles = (roles || []).filter((r) => !ROLE_GUIDE_HIDE.includes((r.name || "").toLowerCase()));
  if (visibleRoles.length === 0) return null;
  const pagesFor = (perms = {}) => {
    const p = { ...perms };
    // The smartHub umbrella grants BOTH tabs — expand it into the named tabs.
    if (p.smartHub) { p.greenfieldAgent = true; p.brownfieldAgent = true; }
    // migrationAgent is the umbrella — grants BOTH migration types.
    if (p.migrationAgent) { p.migrationDotnet = true; p.migrationJitterbit = true; }
    return PERMISSION_PAGE_MAP.filter((m) => p[m.key] === true || p[m.key] === "true").map((m) => m.label);
  };
  return (
    <div className="ta-roleguide">
      <button className="ta-roleguide-toggle" onClick={() => setOpen((o) => !o)}>
        {open ? "▾" : "▸"} Role guide — what each role can see
      </button>
      {open && (
        <table className="ta-roleguide-table">
          <thead>
            <tr>
              <th>Role</th>
              <th>Pages &amp; features it unlocks</th>
            </tr>
          </thead>
          <tbody>
            {visibleRoles.map((r) => {
              const pages = pagesFor(r.permissions);
              return (
                <tr key={r._id || r.name}>
                  <td className="ta-roleguide-name">{r.name}</td>
                  <td>
                    {pages.length ? (
                      <span className="ta-roleguide-chips">
                        {pages.map((pg) => (
                          <span key={pg} className="ta-roleguide-chip">{pg}</span>
                        ))}
                      </span>
                    ) : (
                      <span className="ta-roleguide-none">— no pages</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Mock data for development ─────────────────────────────────────────
const MOCK_DATA = {
  users: [
    {
      userId: "u1", name: "Alice Johnson", email: "alice@acme.com",
      totalInputTokens: 210580, totalOutputTokens: 103590, totalCacheReadTokens: 1365536,
      totalEstimatedCostUSD: 3.1862, totalRequestCount: 10,
      dailyUsage: [
        { date: "2026-04-28", inputTokens: 105290, outputTokens: 51795, cacheReadTokens: 682768, estimatedCostUSD: 1.5931, requestCount: 5 },
        { date: "2026-04-29", inputTokens: 65000, outputTokens: 32000, cacheReadTokens: 400000, estimatedCostUSD: 0.98, requestCount: 3 },
        { date: "2026-04-30", inputTokens: 40290, outputTokens: 19795, cacheReadTokens: 282768, estimatedCostUSD: 0.6131, requestCount: 2 },
      ],
    },
    {
      userId: "u2", name: "Bob Martinez", email: "bob@acme.com",
      totalInputTokens: 88200, totalOutputTokens: 41200, totalCacheReadTokens: 520000,
      totalEstimatedCostUSD: 1.245, totalRequestCount: 6,
      dailyUsage: [
        { date: "2026-04-29", inputTokens: 50000, outputTokens: 24000, cacheReadTokens: 300000, estimatedCostUSD: 0.72, requestCount: 3 },
        { date: "2026-04-30", inputTokens: 38200, outputTokens: 17200, cacheReadTokens: 220000, estimatedCostUSD: 0.525, requestCount: 3 },
      ],
    },
    {
      userId: "u3", name: "Carol Nguyen", email: "carol@acme.com",
      totalInputTokens: 42100, totalOutputTokens: 18900, totalCacheReadTokens: 210000,
      totalEstimatedCostUSD: 0.588, totalRequestCount: 3,
      dailyUsage: [
        { date: "2026-04-30", inputTokens: 42100, outputTokens: 18900, cacheReadTokens: 210000, estimatedCostUSD: 0.588, requestCount: 3 },
      ],
    },
  ],
  summary: {
    totalCost: 5.019, totalRequests: 19, totalUsers: 3,
    totalInputTokens: 340880, totalOutputTokens: 163690, totalCacheReadTokens: 2095536,
  },
};

const USE_MOCK = false;

// ── Helpers ───────────────────────────────────────────────────────────
const fmtTokens = (n) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : String(n);

const fmtCost = (n) => `$${(n || 0).toFixed(4)}`;

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const _ymd = (d) => d.toISOString().slice(0, 10);
const _shift = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d; };
// Quick date-range presets for the dashboard header.
const RANGE_PRESETS = [
  { key: "7d",   label: "Last 7 days",  range: () => ({ from: _ymd(_shift(-6)),  to: today() }) },
  { key: "30d",  label: "Last 30 days", range: () => ({ from: _ymd(_shift(-29)), to: today() }) },
  { key: "lm",   label: "Last month",   range: () => {
      const d = new Date();
      const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
      const last = new Date(d.getFullYear(), d.getMonth(), 0); // day 0 of this month = last day of prev
      return { from: _ymd(first), to: _ymd(last) };
    } },
];

const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; }
};

// ── Role badge ────────────────────────────────────────────────────────
function RoleBadge({ roles }) {
  if (!roles?.length) return <span className="ta-badge ta-badge-default">—</span>;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {roles.map((r) => {
        const name = r?.name || r;
        const label = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
        return <span key={r?._id || name} className="ta-badge ta-badge-role">{label}</span>;
      })}
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    processing: { label: "Processing", cls: "ta-badge-warning" },
    pending:    { label: "Pending",    cls: "ta-badge-warning" },
    active:     { label: "Active",     cls: "ta-badge-success" },
    granted:    { label: "Granted",    cls: "ta-badge-success" },
    approved:   { label: "Approved",   cls: "ta-badge-success" },
    denied:     { label: "Denied",     cls: "ta-badge-error" },
    rejected:   { label: "Rejected",   cls: "ta-badge-error" },
  };
  const { label, cls } = map[(status || "").toLowerCase()] || { label: status || "—", cls: "ta-badge-default" };
  return <span className={`ta-badge ${cls}`}>{label}</span>;
}

// ── Summary card ──────────────────────────────────────────────────────
function SummaryCard({ icon, label, value, accent }) {
  return (
    <div className="ta-card" style={{ borderTop: `3px solid ${accent}` }}>
      <div className="ta-card-icon" style={{ color: accent }}>{icon}</div>
      <div className="ta-card-body">
        <div className="ta-card-value">{value}</div>
        <div className="ta-card-label">{label}</div>
      </div>
    </div>
  );
}

// ── Expandable user row (Cost Dashboard) ──────────────────────────────
function UserRow({ user, rank }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className={`ta-tr ta-tr-user${expanded ? " expanded" : ""}`}
        onClick={() => setExpanded((e) => !e)}
        style={{ cursor: "pointer" }}
      >
        <td className="ta-td ta-td-rank"><span className="ta-rank">{rank}</span></td>
        <td className="ta-td">
          <div className="ta-user-info">
            <div className="ta-avatar">{(user.name || user.email || "?")[0].toUpperCase()}</div>
            <div>
              <div className="ta-user-name">{user.name || "—"}</div>
              <div className="ta-user-email">{user.email}</div>
            </div>
          </div>
        </td>
        <td className="ta-td ta-td-num">{user.totalRequestCount}</td>
        <td className="ta-td ta-td-num">{fmtTokens(user.totalInputTokens + user.totalOutputTokens)}</td>
        <td className="ta-td ta-td-num">{fmtTokens(user.totalCacheReadTokens)}</td>
        <td className="ta-td ta-td-cost">
          <span className="ta-cost-val">{fmtCost(user.totalEstimatedCostUSD)}</span>
        </td>
        <td className="ta-td ta-td-chevron">
          <span className="ta-chevron">{expanded ? "▾" : "▸"}</span>
        </td>
      </tr>

      {expanded && (
        <tr className="ta-tr ta-tr-detail">
          <td colSpan={7} className="ta-td-detail">
            <table className="ta-daily-table">
              <thead>
                <tr>
                  <th>Date</th><th>Requests</th><th>Input tokens</th>
                  <th>Output tokens</th><th>Total tokens</th><th>Cache read</th><th>Est. cost</th>
                </tr>
              </thead>
              <tbody>
                {(user.dailyUsage || []).slice().sort((a, b) => b.date.localeCompare(a.date)).map((d) => (
                  <tr key={d.date}>
                    <td>{d.date}</td>
                    <td>{d.requestCount}</td>
                    <td>{fmtTokens(d.inputTokens)}</td>
                    <td>{fmtTokens(d.outputTokens)}</td>
                    <td>{fmtTokens(d.inputTokens + d.outputTokens)}</td>
                    <td>{fmtTokens(d.cacheReadTokens)}</td>
                    <td className="ta-daily-cost">{fmtCost(d.estimatedCostUSD)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

// ── User Approvals tab ────────────────────────────────────────────────
function UserApprovalsTab({ tenantId, token }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [availableRoles, setAvailableRoles] = useState([]);
  const [editingRoleUserId, setEditingRoleUserId] = useState(null);
  const [editRoleValues, setEditRoleValues] = useState([]);
  const [roleEditLoading, setRoleEditLoading] = useState(false);

  // Team / project assignment
  const [teams, setTeams] = useState([]);
  const [editingTeamsUserId, setEditingTeamsUserId] = useState(null);
  const [editTeamValues, setEditTeamValues] = useState([]);
  const [teamEditLoading, setTeamEditLoading] = useState(false);

  // Invite state
  const [seat, setSeat] = useState({ invitations: [], usersCount: 0, pendingCount: 0, maxUsers: null });
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRoleIds, setInviteRoleIds] = useState([]);
  const [inviteAuthProvider, setInviteAuthProvider] = useState("local"); // 'local' | 'google'
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteSuccess, setInviteSuccess] = useState("");
  const [emailFocus, setEmailFocus] = useState(false);
  const EMAIL_RE = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i;

  const fetchInvites = useCallback(() => {
    fetch(`${domain}/api/v1/tenant/invitations`, { headers: { "x-access-token": token } })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d) setSeat({ invitations: d.invitations || [], usersCount: d.usersCount || 0, pendingCount: d.pendingCount || 0, maxUsers: d.maxUsers ?? null }); })
      .catch(() => {});
  }, [token]);

  useEffect(() => { fetchInvites(); }, [fetchInvites]);

  const toggleInviteRole = (roleId) => {
    setInviteRoleIds((prev) => prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]);
  };

  const sendInvite = async () => {
    setInviteError("");
    setInviteSuccess("");
    if (!inviteEmail.trim()) { setInviteError("Please enter an email address."); return; }
    if (!EMAIL_RE.test(inviteEmail.trim())) { setInviteError("Enter a valid email address."); return; }
    setInviting(true);
    try {
      const res = await fetch(`${domain}/api/v1/invitations`, {
        method: "POST",
        headers: { "x-access-token": token, "Content-Type": "application/json" },
        body: JSON.stringify({ email: inviteEmail.trim(), roleIds: inviteRoleIds, tenantRole: "developer", authProvider: inviteAuthProvider }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not send invitation.");
      setInviteSuccess(`Invitation sent to ${inviteEmail.trim()}.`);
      setInviteEmail("");
      setInviteRoleIds([]);
      fetchInvites();
    } catch (e) {
      setInviteError(e.message);
    } finally {
      setInviting(false);
    }
  };

  const fetchUsers = useCallback(() => {
    setLoading(true);
    setError(null);
    const fetchUrl = `${domain}/api/v1/tenant/users?tenantId=${tenantId}`;
    console.log("[UserApprovals] REQUEST →", fetchUrl);
    fetch(fetchUrl, { headers: { "x-access-token": token } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => {
        console.log("[UserApprovals] RESPONSE ←", d);
        const list = Array.isArray(d) ? d : (d.users || []);
        console.log("[UserApprovals] all users →", list);
        setUsers(list);
      })
      .catch((e) => { console.error("[UserApprovals] ERROR", e); setError(e.message); })
      .finally(() => setLoading(false));
  }, [tenantId, token]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    fetch(`${domain}/api/v1/roles`, { headers: { "x-access-token": token } })
      .then((r) => r.ok ? r.json() : [])
      .then((d) => setAvailableRoles(Array.isArray(d) ? d : (d.roles || [])))
      .catch(() => {});
  }, [token]);

  // Load the tenant's teams so users can be assigned to them.
  useEffect(() => {
    fetch(`${domain}/api/v1/tenant/teams`, { headers: { "x-access-token": token } })
      .then((r) => r.ok ? r.json() : [])
      .then((d) => setTeams(Array.isArray(d) ? d : (d.teams || [])))
      .catch(() => {});
  }, [token]);

  const teamNameById = (id) => (teams.find((t) => t._id === id) || {}).name || id;

  const openTeamsEdit = (uid, currentTeamIds) => {
    setEditingTeamsUserId(uid);
    setEditTeamValues((currentTeamIds || []).map(String));
  };
  const cancelTeamsEdit = () => { setEditingTeamsUserId(null); setEditTeamValues([]); };
  const toggleEditTeam = (teamId) => {
    setEditTeamValues((prev) => prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]);
  };

  const handleTeamsUpdate = async (userId) => {
    setTeamEditLoading(true);
    try {
      const res = await fetch(`${domain}/api/v1/tenant/users/${userId}/teams`, {
        method: "PATCH",
        headers: { "x-access-token": token, "Content-Type": "application/json" },
        body: JSON.stringify({ teamIds: editTeamValues }),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.message || `HTTP ${res.status}`);
      const newTeamIds = result.teamIds || editTeamValues;
      setUsers((prev) => prev.map((u) => ((u._id || u.id) === userId ? { ...u, teamIds: newTeamIds } : u)));
      cancelTeamsEdit();
    } catch (e) {
      alert(`Team update failed: ${e.message}`);
    } finally {
      setTeamEditLoading(false);
    }
  };

  const openRoleEdit = (uid, currentRoles) => {
    setEditingRoleUserId(uid);
    setEditRoleValues((currentRoles || []).map((r) => r._id));
  };

  const cancelRoleEdit = () => {
    setEditingRoleUserId(null);
    setEditRoleValues([]);
  };

  const toggleEditRole = (roleId) => {
    setEditRoleValues((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const handleRoleUpdate = async (userId) => {
    if (!editRoleValues.length) return;
    setRoleEditLoading(true);
    try {
      const res = await fetch(`${domain}/api/v1/tenant/users/${userId}/role`, {
        method: "PATCH",
        headers: { "x-access-token": token, "Content-Type": "application/json" },
        body: JSON.stringify({ roleIds: editRoleValues }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json().catch(() => ({}));
      const updatedRoles = result.roles || availableRoles.filter((r) => editRoleValues.includes(r._id));
      setUsers((prev) => prev.map((u) =>
        (u._id || u.id) === userId ? { ...u, roles: updatedRoles } : u
      ));
      cancelRoleEdit();
    } catch (e) {
      alert(`Role update failed: ${e.message}`);
    } finally {
      setRoleEditLoading(false);
    }
  };

  const handleAction = async (userId, action) => {
    const payload = { status: action === "grant" ? "granted" : action === "revoke" ? "processing" : "denied" };
    console.log(`[UserApprovals] ${action.toUpperCase()} user ${userId} →`, payload);
    setActionLoading((prev) => ({ ...prev, [userId]: action }));
    try {
      const res = await fetch(`${domain}/api/v1/tenant/users/${userId}/permission`, {
        method: "PATCH",
        headers: { "x-access-token": token, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json().catch(() => ({}));
      console.log(`[UserApprovals] ${action.toUpperCase()} response ←`, result);
      setUsers((prev) => prev.map((u) =>
        (u._id || u.id) === userId
          ? { ...u, permission: payload.status, ...(result.roles ? { roles: result.roles } : {}) }
          : u
      ));
    } catch (e) {
      console.error(`[UserApprovals] ${action.toUpperCase()} ERROR`, e);
      alert(`Action failed: ${e.message}`);
    } finally {
      setActionLoading((prev) => { const n = { ...prev }; delete n[userId]; return n; });
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This cannot be undone.`)) return;
    setActionLoading((prev) => ({ ...prev, [userId]: "delete" }));
    try {
      const res = await fetch(`${domain}/api/v1/tenant/users/${userId}`, {
        method: "DELETE",
        headers: { "x-access-token": token },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setUsers((prev) => prev.filter((u) => (u._id || u.id) !== userId));
    } catch (e) {
      console.error("[UserApprovals] DELETE ERROR", e);
      alert(`Delete failed: ${e.message}`);
    } finally {
      setActionLoading((prev) => { const n = { ...prev }; delete n[userId]; return n; });
    }
  };

  if (loading) return <div className="ta-loading"><div className="ta-spinner" /><span>Loading users…</span></div>;
  if (error)   return <div className="ta-error">⚠ {error}</div>;
  if (!users.length) return <div className="ta-empty">No users found.</div>;

  const pending = users.filter((u) => (u.permission || "").toLowerCase() === "processing");

  const seatsUsed = (seat.usersCount || users.length) + (seat.pendingCount || 0);
  const atCap = seat.maxUsers != null && seatsUsed >= seat.maxUsers;

  return (
    <div className="ta-table-wrap">
      {/* ── Invite users ── */}
      <div style={{ padding: "14px 16px", marginBottom: 16, background: "#0F1F2E", border: "1px solid #1e3044", borderRadius: 10 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#E5EAEF" }}>Invite users</div>
            <div style={{ fontSize: 12, color: "#8a9bb0", marginTop: 2 }}>
              Seats used: <strong style={{ color: atCap ? "#f59e0b" : "#2EBFA5" }}>{seatsUsed}{seat.maxUsers != null ? ` / ${seat.maxUsers}` : ""}</strong>
              {seat.pendingCount ? <span> · {seat.pendingCount} pending</span> : null}
            </div>
          </div>
          <button
            onClick={() => { setShowInvite((v) => !v); setInviteError(""); setInviteSuccess(""); }}
            disabled={atCap}
            title={atCap ? "Seat limit reached — upgrade the plan to invite more" : ""}
            style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: atCap ? "#1e3044" : "#2EBFA5", color: atCap ? "#6b849a" : "#0F1F2E", fontSize: 13, fontWeight: 700, cursor: atCap ? "default" : "pointer" }}>
            {showInvite ? "Close" : "+ Invite user"}
          </button>
        </div>

        {showInvite && !atCap && (
          <div style={{ marginTop: 14, borderTop: "1px solid #1e3044", paddingTop: 14 }}>
            {(() => {
              const emailErr = !!inviteError && !EMAIL_RE.test(inviteEmail.trim());
              const emailBorder = emailErr ? "#ef4444" : emailFocus ? "#2EBFA5" : "#2a3f57";
              return (
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
              <div style={{ flex: "1 1 260px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#c7d2de", marginBottom: 7 }}>
                  Email <span style={{ color: "#ef4444" }}>*</span>
                </div>
                <input
                  value={inviteEmail}
                  onChange={(e) => { setInviteEmail(e.target.value); if (inviteError) setInviteError(""); }}
                  onFocus={() => setEmailFocus(true)}
                  onBlur={() => setEmailFocus(false)}
                  onKeyDown={(e) => { if (e.key === "Enter") sendInvite(); }}
                  placeholder="teammate@company.com"
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 8, border: `1.5px solid ${emailBorder}`, background: "#0f1b28", color: "#E5EAEF", fontSize: 14, boxSizing: "border-box", outline: "none", boxShadow: emailFocus && !emailErr ? "0 0 0 3px rgba(46,191,165,0.12)" : "none", transition: "border-color 0.15s, box-shadow 0.15s" }}
                />
                {emailErr && <div style={{ color: "#ef4444", fontSize: 12.5, marginTop: 6 }}>{inviteError}</div>}
              </div>
              <div style={{ flex: "1 1 180px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#c7d2de", marginBottom: 7 }}>Sign-in method</div>
                <select
                  value={inviteAuthProvider}
                  onChange={(e) => setInviteAuthProvider(e.target.value)}
                  style={{ width: "100%", padding: "12px 14px", borderRadius: 8, border: "1.5px solid #2a3f57", background: "#0f1b28", color: "#E5EAEF", fontSize: 14, boxSizing: "border-box", outline: "none" }}>
                  <option value="local">Password (Keycloak)</option>
                  <option value="google">Google</option>
                </select>
                <div style={{ fontSize: 11.5, color: "#6b849a", marginTop: 8 }}>Google users sign in with Google; no password is set.</div>
              </div>
              <div style={{ flex: "1 1 280px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#c7d2de", marginBottom: 7 }}>Permissions (roles)</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {availableRoles.map((r) => {
                    const sel = inviteRoleIds.includes(r._id);
                    return (
                      <button
                        key={r._id}
                        type="button"
                        onClick={() => toggleInviteRole(r._id)}
                        style={{ padding: "6px 13px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                          border: sel ? "1.5px solid #2EBFA5" : "1.5px solid #2a3f57",
                          background: sel ? "rgba(46,191,165,0.15)" : "transparent",
                          color: sel ? "#2EBFA5" : "#a9b7c6", transition: "all 0.12s" }}>
                        {sel ? "✓ " : ""}{r.name}
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11.5, color: "#6b849a", marginTop: 8 }}>Leave empty to assign the default <code>user</code> role.</div>
              </div>
            </div>
              );
            })()}
            {/* Non-email errors (seat cap, server/network) — the email-format error renders inline above. */}
            {inviteError && EMAIL_RE.test(inviteEmail.trim()) && <div style={{ color: "#ef4444", fontSize: 13, marginTop: 12 }}>{inviteError}</div>}
            {inviteSuccess && <div style={{ color: "#2EBFA5", fontSize: 13, marginTop: 12 }}>{inviteSuccess}</div>}
            <button
              onClick={sendInvite}
              disabled={inviting}
              style={{ marginTop: 14, padding: "10px 22px", borderRadius: 8, border: "none", background: inviting ? "#1e3044" : "#3b7fff", color: inviting ? "#6b849a" : "#fff", fontSize: 13.5, fontWeight: 700, cursor: inviting ? "default" : "pointer" }}>
              {inviting ? "Sending…" : "Send invitation"}
            </button>
          </div>
        )}

        {seat.invitations && seat.invitations.length > 0 && (
          <div style={{ marginTop: 14, borderTop: "1px solid #1e3044", paddingTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#8a9bb0", marginBottom: 8 }}>Pending invitations</div>
            {seat.invitations.map((inv) => (
              <div key={inv._id || inv.email} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#dce8ff", padding: "4px 0" }}>
                <span>{inv.email} <span style={{ color: "#6b849a" }}>· {inv.tenantRole || "developer"}</span></span>
                <span style={{ color: "#6b849a" }}>invited {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <RoleGuide roles={availableRoles} />

      <div className="ta-table-header">
        <span className="ta-table-title">User Permissions</span>
        <span className="ta-table-meta">{users.length} users · {pending.length} pending</span>
      </div>
      <table className="ta-table">
        <thead>
          <tr>
            <th className="ta-th ta-th-rank">#</th>
            <th className="ta-th">User</th>
            <th className="ta-th ta-th-role">Role</th>
            <th className="ta-th">Teams / Projects</th>
            <th className="ta-th ta-th-status">Status</th>
            <th className="ta-th ta-th-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u, i) => {
            const uid = u._id || u.id;
            const busy = actionLoading[uid];
            const status = (u.permission || u.status || "").toLowerCase();
            const isActive = status === "active" || status === "granted";
            const isProcessing = status === "processing";
            return (
              <tr key={uid} className="ta-tr ta-tr-user">
                <td className="ta-td ta-td-rank"><span className="ta-rank">{i + 1}</span></td>
                <td className="ta-td">
                  <div className="ta-user-info">
                    <div className="ta-avatar">{(u.username || u.name || u.email || "?")[0].toUpperCase()}</div>
                    <div>
                      <div className="ta-user-name">{u.username || u.name || "—"}</div>
                      <div className="ta-user-email">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="ta-td ta-td-role">
                  {editingRoleUserId === uid ? (
                    <div className="ta-role-edit">
                      <div className="ta-role-checklist">
                        {availableRoles.map((r) => (
                          <label key={r._id} className="ta-role-check">
                            <input
                              type="checkbox"
                              checked={editRoleValues.includes(r._id)}
                              onChange={() => toggleEditRole(r._id)}
                              disabled={roleEditLoading}
                            />
                            <span>{r.name}</span>
                          </label>
                        ))}
                      </div>
                      <button className="ta-role-save" disabled={roleEditLoading || !editRoleValues.length} onClick={() => handleRoleUpdate(uid)}>
                        {roleEditLoading ? "…" : "✓"}
                      </button>
                      <button className="ta-role-cancel" disabled={roleEditLoading} onClick={cancelRoleEdit}>✕</button>
                    </div>
                  ) : (
                    <div className="ta-role-view">
                      <RoleBadge roles={u.roles} />
                      <button className="ta-role-edit-btn" title="Edit role" onClick={() => openRoleEdit(uid, u.roles)}>✏️</button>
                    </div>
                  )}
                </td>
                <td className="ta-td">
                  {editingTeamsUserId === uid ? (
                    <div className="ta-role-edit">
                      <div className="ta-role-checklist">
                        {teams.length === 0 && <span style={{ fontSize: 12, color: "#7a93bc" }}>No teams yet — create some in the Teams / Projects tab.</span>}
                        {teams.map((t) => (
                          <label key={t._id} className="ta-role-check">
                            <input
                              type="checkbox"
                              checked={editTeamValues.includes(t._id)}
                              onChange={() => toggleEditTeam(t._id)}
                              disabled={teamEditLoading}
                            />
                            <span>{t.name}</span>
                          </label>
                        ))}
                      </div>
                      <button className="ta-role-save" disabled={teamEditLoading} onClick={() => handleTeamsUpdate(uid)}>
                        {teamEditLoading ? "…" : "✓"}
                      </button>
                      <button className="ta-role-cancel" disabled={teamEditLoading} onClick={cancelTeamsEdit}>✕</button>
                    </div>
                  ) : (
                    <div className="ta-role-view">
                      {(u.teamIds && u.teamIds.length) ? (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {u.teamIds.map((tid) => (
                            <span key={tid} className="ta-badge ta-badge-role">{teamNameById(tid)}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="ta-badge ta-badge-default">—</span>
                      )}
                      <button className="ta-role-edit-btn" title="Assign teams" onClick={() => openTeamsEdit(uid, u.teamIds)}>✏️</button>
                    </div>
                  )}
                </td>
                <td className="ta-td ta-td-status"><StatusBadge status={u.permission || u.status} /></td>
                <td className="ta-td ta-td-actions">
                  <div className="ta-action-row">
                    {isActive && (
                      <button className="ta-btn-deny" disabled={!!busy} onClick={() => handleAction(uid, "revoke")}>
                        {busy === "revoke" ? "…" : "Revoke"}
                      </button>
                    )}
                    {isProcessing && (
                      <>
                        <button className="ta-btn-grant" disabled={!!busy} onClick={() => handleAction(uid, "grant")}>
                          {busy === "grant" ? "…" : "Grant"}
                        </button>
                        <button className="ta-btn-deny" disabled={!!busy} onClick={() => handleAction(uid, "deny")}>
                          {busy === "deny" ? "…" : "Deny"}
                        </button>
                      </>
                    )}
                    {!isActive && !isProcessing && (
                      <button className="ta-btn-grant" disabled={!!busy} onClick={() => handleAction(uid, "grant")}>
                        {busy === "grant" ? "…" : "Grant"}
                      </button>
                    )}
                    <button
                      className="ta-btn-delete"
                      disabled={!!busy}
                      onClick={() => handleDelete(uid, u.username || u.name || u.email)}
                    >
                      {busy === "delete" ? "…" : "Delete"}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Token extension requests table (shared) ───────────────────────────
function TokenRequestsTable({ rows, actionLoading, onAction, showActions }) {
  return (
    <table className="ta-table">
      <thead>
        <tr>
          <th className="ta-th ta-th-rank">#</th>
          <th className="ta-th">User</th>
          <th className="ta-th">Requested On</th>
          <th className="ta-th ta-th-num">Tokens Requested</th>
          <th className="ta-th">Status</th>
          {showActions && <th className="ta-th ta-th-actions">Actions</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const id = r._id || r.id;
          const busy = actionLoading[id];
          const dateStr = r.requestedAt || r.createdAt;
          // userId may be a populated object {_id, username, email} or a plain string
          const userObj = r.userId && typeof r.userId === "object" ? r.userId : null;
          const displayName = r.userName || r.name || userObj?.username || "Deleted user";
          const displayEmail = r.email || userObj?.email || "—";
          return (
            <tr key={id} className="ta-tr ta-tr-user">
              <td className="ta-td ta-td-rank"><span className="ta-rank">{i + 1}</span></td>
              <td className="ta-td">
                <div className="ta-user-info">
                  <div className="ta-avatar">{(displayName !== "—" ? displayName : displayEmail)[0].toUpperCase()}</div>
                  <div>
                    <div className="ta-user-name">{displayName}</div>
                    <div className="ta-user-email">{displayEmail}</div>
                  </div>
                </div>
              </td>
              <td className="ta-td">{dateStr ? new Date(dateStr).toLocaleDateString() : "—"}</td>
              <td className="ta-td ta-td-num">{r.extensionTokens ? fmtTokens(r.extensionTokens) : "—"}</td>
              <td className="ta-td"><StatusBadge status={r.status} /></td>
              {showActions && (
                <td className="ta-td ta-td-actions">
                  <div className="ta-action-row">
                    <button className="ta-btn-grant" disabled={!!busy} onClick={() => onAction(id, "approve")}>
                      {busy === "approve" ? "…" : "Approve"}
                    </button>
                    <button className="ta-btn-deny" disabled={!!busy} onClick={() => onAction(id, "reject")}>
                      {busy === "reject" ? "…" : "Reject"}
                    </button>
                  </div>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Token Extensions tab ──────────────────────────────────────────────
function TokenExtensionsTab({ tenantId, token }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const fetchRequests = useCallback(() => {
    setLoading(true);
    setError(null);
    const fetchUrl = `${domain}/api/v1/token/extension-requests?tenantId=${tenantId}`;
    console.log("[TokenExtensions] REQUEST →", fetchUrl);
    fetch(fetchUrl, { headers: { "x-access-token": token } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => {
        console.log("[TokenExtensions] RESPONSE ←", d);
        setRequests(Array.isArray(d) ? d : (d.requests || []));
      })
      .catch((e) => { console.error("[TokenExtensions] ERROR", e); setError(e.message); })
      .finally(() => setLoading(false));
  }, [tenantId, token]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleAction = async (id, action) => {
    const actionUrl = `${domain}/api/v1/token/extension-requests/${id}/${action}`;
    console.log(`[TokenExtensions] ${action.toUpperCase()} request ${id} →`, actionUrl);
    setActionLoading((prev) => ({ ...prev, [id]: action }));
    try {
      const res = await fetch(actionUrl, {
        method: "POST",
        headers: { "x-access-token": token },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json().catch(() => ({}));
      console.log(`[TokenExtensions] ${action.toUpperCase()} response ←`, result);
      setRequests((prev) =>
        prev.map((r) => (r._id || r.id) === id
          ? { ...r, status: action === "approve" ? "approved" : "rejected" }
          : r
        )
      );
    } catch (e) {
      console.error(`[TokenExtensions] ${action.toUpperCase()} ERROR`, e);
      alert(`Action failed: ${e.message}`);
    } finally {
      setActionLoading((prev) => { const n = { ...prev }; delete n[id]; return n; });
    }
  };

  if (loading) return <div className="ta-loading"><div className="ta-spinner" /><span>Loading requests…</span></div>;
  if (error)   return <div className="ta-error">⚠ {error}</div>;
  if (!requests.length) return <div className="ta-empty">No token extension requests.</div>;

  const pending  = requests.filter((r) => (r.status || "").toLowerCase() === "pending");
  const resolved = requests.filter((r) => (r.status || "").toLowerCase() !== "pending");

  return (
    <>
      {pending.length > 0 && (
        <div className="ta-table-wrap" style={{ marginBottom: 20 }}>
          <div className="ta-table-header">
            <span className="ta-table-title">Pending Requests</span>
            <span className="ta-table-meta">{pending.length} pending</span>
          </div>
          <TokenRequestsTable rows={pending} actionLoading={actionLoading} onAction={handleAction} showActions />
        </div>
      )}
      {resolved.length > 0 && (
        <div className="ta-table-wrap">
          <div className="ta-table-header">
            <span className="ta-table-title">Request History</span>
            <span className="ta-table-meta">{resolved.length} resolved</span>
          </div>
          <TokenRequestsTable rows={resolved} actionLoading={actionLoading} onAction={handleAction} showActions={false} />
        </div>
      )}
    </>
  );
}

// ── Teams / Projects tab ──────────────────────────────────────────────
function TeamsTab({ tenantId, token }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [rowBusy, setRowBusy] = useState({});

  const fetchTeams = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(`${domain}/api/v1/tenant/teams`, { headers: { "x-access-token": token } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => setTeams(Array.isArray(d) ? d : (d.teams || [])))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  const createTeam = async () => {
    setCreateError("");
    if (!name.trim()) { setCreateError("Please enter a team name."); return; }
    setCreating(true);
    try {
      const res = await fetch(`${domain}/api/v1/tenant/teams`, {
        method: "POST",
        headers: { "x-access-token": token, "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), description: description.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not create team.");
      setName("");
      setDescription("");
      fetchTeams();
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (t) => { setEditingId(t._id); setEditName(t.name || ""); setEditDesc(t.description || ""); };
  const cancelEdit = () => { setEditingId(null); setEditName(""); setEditDesc(""); };

  const saveEdit = async (teamId) => {
    if (!editName.trim()) return;
    setRowBusy((p) => ({ ...p, [teamId]: "save" }));
    try {
      const res = await fetch(`${domain}/api/v1/tenant/teams/${teamId}`, {
        method: "PATCH",
        headers: { "x-access-token": token, "Content-Type": "application/json" },
        body: JSON.stringify({ name: editName.trim(), description: editDesc.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      setTeams((prev) => prev.map((t) => (t._id === teamId ? { ...t, ...data } : t)));
      cancelEdit();
    } catch (e) {
      alert(`Update failed: ${e.message}`);
    } finally {
      setRowBusy((p) => { const n = { ...p }; delete n[teamId]; return n; });
    }
  };

  const deleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Delete team "${teamName}"? Members will be unassigned from it. This cannot be undone.`)) return;
    setRowBusy((p) => ({ ...p, [teamId]: "delete" }));
    try {
      const res = await fetch(`${domain}/api/v1/tenant/teams/${teamId}`, {
        method: "DELETE",
        headers: { "x-access-token": token },
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.message || `HTTP ${res.status}`); }
      setTeams((prev) => prev.filter((t) => t._id !== teamId));
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    } finally {
      setRowBusy((p) => { const n = { ...p }; delete n[teamId]; return n; });
    }
  };

  return (
    <div className="ta-table-wrap">
      {/* Create team */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", padding: "16px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <label className="ta-date-label">Team / Project name</label>
          <input className="ta-date-input" style={{ minWidth: 200 }} value={name} placeholder="e.g. Payments Squad"
            onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") createTeam(); }} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 220 }}>
          <label className="ta-date-label">Description (optional)</label>
          <input className="ta-date-input" value={description} placeholder="What this team/project is for"
            onChange={(e) => setDescription(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") createTeam(); }} />
        </div>
        <button className="ta-apply-btn" onClick={createTeam} disabled={creating || !name.trim()}>
          {creating ? "Creating…" : "+ Create Team"}
        </button>
      </div>
      {createError && <div className="ta-error" style={{ margin: "10px 16px" }}>⚠ {createError}</div>}

      <div className="ta-table-header">
        <span className="ta-table-title">Teams / Projects</span>
        <span className="ta-table-meta">{teams.length} team{teams.length !== 1 ? "s" : ""}</span>
      </div>

      {error && <div className="ta-error">⚠ {error}</div>}
      {loading && !teams.length && (
        <div className="ta-loading"><div className="ta-spinner" /><span>Fetching teams…</span></div>
      )}
      {!loading && !teams.length && !error && (
        <div className="ta-empty">No teams yet. Create one above, then assign users to it from the User Approvals tab.</div>
      )}

      {teams.length > 0 && (
        <table className="ta-table">
          <thead>
            <tr>
              <th className="ta-th">Name</th>
              <th className="ta-th">Description</th>
              <th className="ta-th ta-th-num">Members</th>
              <th className="ta-th ta-th-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {teams.map((t) => {
              const busy = rowBusy[t._id];
              const isEditing = editingId === t._id;
              return (
                <tr key={t._id} className="ta-tr">
                  <td className="ta-td">
                    {isEditing
                      ? <input className="ta-date-input" value={editName} onChange={(e) => setEditName(e.target.value)} />
                      : <span className="ta-user-name">{t.name}</span>}
                  </td>
                  <td className="ta-td">
                    {isEditing
                      ? <input className="ta-date-input" style={{ width: "100%" }} value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
                      : <span style={{ color: "#7a93bc", fontSize: 13 }}>{t.description || "—"}</span>}
                  </td>
                  <td className="ta-td ta-td-num"><span className="ta-badge ta-badge-default">{t.memberCount ?? 0}</span></td>
                  <td className="ta-td ta-td-actions">
                    <div className="ta-action-row">
                      {isEditing ? (
                        <>
                          <button className="ta-btn-grant" disabled={busy === "save" || !editName.trim()} onClick={() => saveEdit(t._id)}>
                            {busy === "save" ? "…" : "Save"}
                          </button>
                          <button className="ta-btn-deny" disabled={busy === "save"} onClick={cancelEdit}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className="ta-role-edit-btn" title="Edit" onClick={() => openEdit(t)}>✏️</button>
                          <button className="ta-btn-delete" disabled={!!busy} onClick={() => deleteTeam(t._id, t.name)}>
                            {busy === "delete" ? "…" : "Delete"}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Adoption tab ──────────────────────────────────────────────────────
const shortDate = (s) => {
  if (!s) return "—";
  const d = new Date(s + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const CLASS_META = {
  "one-time":  { label: "One-time",  cls: "ta-badge-warning" },
  "returning": { label: "Returning", cls: "ta-badge-role" },
  "sticky":    { label: "Sticky",    cls: "ta-badge-success" },
  "inactive":  { label: "Inactive",  cls: "ta-badge-default" },
};

// Linearly interpolate the target ramp [start, mid, goal] across `count` weeks.
function rampTargets([start, mid, goal], count) {
  if (count <= 0) return [];
  if (count === 1) return [goal];
  const out = [];
  for (let i = 0; i < count; i++) {
    const t = i / (count - 1);
    const v = t <= 0.5 ? start + (mid - start) * (t / 0.5) : mid + (goal - mid) * ((t - 0.5) / 0.5);
    out.push(Math.round(v));
  }
  return out;
}

function AdoptionTab({ tenantId, token, from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");

  const TARGET_KEY = `ta_adoption_targets_${tenantId}`;
  const [targets, setTargets] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(TARGET_KEY) || "null"); if (Array.isArray(v) && v.length === 3) return v; } catch {}
    return [20, 60, 90];
  });
  const saveTargets = (next) => { setTargets(next); try { localStorage.setItem(TARGET_KEY, JSON.stringify(next)); } catch {} };

  useEffect(() => {
    fetch(`${domain}/api/v1/tenant/teams`, { headers: { "x-access-token": token } })
      .then((r) => r.ok ? r.json() : [])
      .then((d) => setTeams(Array.isArray(d) ? d : (d.teams || [])))
      .catch(() => {});
  }, [token]);

  const fetchAdoption = useCallback(() => {
    setLoading(true);
    setError(null);
    const qs = [`from=${from}`, `to=${to}`];
    if (teamId) qs.push(`teamId=${teamId}`);
    fetch(`${domain}/api/v1/tenant/adoption?${qs.join("&")}`, { headers: { "x-access-token": token } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => setData(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [from, to, teamId, token]);

  useEffect(() => { fetchAdoption(); }, [fetchAdoption]);

  const weeks = data?.weeks || [];
  const targetSeries = rampTargets(targets, weeks.length);
  const teamNameById = (id) => (teams.find((t) => t._id === id) || {}).name || id;
  const latestPct = weeks.length ? weeks[weeks.length - 1].adoptionPct : (data?.adoptionPct ?? 0);
  const latestTarget = targetSeries.length ? targetSeries[targetSeries.length - 1] : (targets[2] || 0);

  return (
    <div>
      {/* Controls */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
        <label className="ta-date-label">Team / Project</label>
        <select className="ta-date-input" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">All teams</option>
          {teams.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
        </select>
      </div>

      {error && <div className="ta-error">⚠ {error}</div>}
      {loading && !data && <div className="ta-loading"><div className="ta-spinner" /><span>Fetching adoption…</span></div>}

      {data && (
        <>
          <div className="ta-cards">
            <SummaryCard icon="🧑‍💻" label={`Active Users / ${data.totalUsers} seats`} value={data.activeUsers} accent="#4a90d9" />
            <SummaryCard icon="📈" label={`Adoption % (target ${latestTarget}%)`} value={`${data.adoptionPct}%`} accent={data.adoptionPct >= latestTarget ? "#2ebfa5" : "#ffa032"} />
            <SummaryCard icon="🟢" label="Active Today (DAU)" value={data.dau?.count ?? 0} accent="#63b3ed" />
            <SummaryCard icon="1️⃣" label="One-time Users" value={data.buckets?.oneTime ?? 0} accent="#ffa032" />
            <SummaryCard icon="⭐" label="Sticky Users" value={data.buckets?.sticky ?? 0} accent="#9a75f6" />
          </div>

          {/* Weekly active users vs target */}
          <div className="ta-table-wrap" style={{ marginTop: 8 }}>
            <div className="ta-table-header" style={{ alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
              <div>
                <span className="ta-table-title">Weekly Active Users</span>
                <div className="ta-table-meta" style={{ marginTop: 2 }}>{weeks.length} weeks · adoption % of active seats</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <label className="ta-date-label" title="Your rollout goal — sets the orange target marker on each bar">🎯 Goal ramp %</label>
                  {["Start", "Mid", "Goal"].map((lbl, i) => (
                    <input key={lbl} type="number" min="0" max="100" title={`${lbl} target %`} className="ta-date-input" style={{ width: 60 }}
                      value={targets[i]} onChange={(e) => { const n = [...targets]; n[i] = Number(e.target.value); saveTargets(n); }} />
                  ))}
                </div>
                <span style={{ fontSize: 10.5, color: "#3d5273" }}>Your adoption goal — draws the orange marker on each bar (saved to this browser)</span>
              </div>
            </div>
            {weeks.length === 0 ? (
              <div className="ta-empty">No activity in this range yet.</div>
            ) : (
              <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
                {weeks.map((w, i) => {
                  const tgt = targetSeries[i] ?? 0;
                  const hit = w.adoptionPct >= tgt;
                  return (
                    <div key={w.weekStart} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 56, fontSize: 11, color: "#7a93bc", fontFamily: "monospace", flexShrink: 0 }}>{shortDate(w.weekStart)}</div>
                      <div style={{ position: "relative", flex: 1, height: 22, background: "rgba(255,255,255,0.05)", borderRadius: 6, overflow: "hidden" }}>
                        <div style={{ width: `${Math.min(w.adoptionPct, 100)}%`, height: "100%", background: hit ? "#2ebfa5" : "#4a90d9", transition: "width .3s" }} />
                        {/* target marker */}
                        <div title={`Target ${tgt}%`} style={{ position: "absolute", top: -2, bottom: -2, left: `${Math.min(tgt, 100)}%`, width: 2, background: "#ffa032" }} />
                      </div>
                      <div style={{ width: 128, fontSize: 11, color: "#dce8ff", textAlign: "right", flexShrink: 0 }}>
                        {w.activeUsers} active · <strong style={{ color: hit ? "#2ebfa5" : "#ffa032" }}>{w.adoptionPct}%</strong> <span style={{ color: "#3d5273" }}>/ {tgt}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Stickiness table */}
          <div className="ta-table-wrap" style={{ marginTop: 16 }}>
            <div className="ta-table-header">
              <span className="ta-table-title">User Stickiness</span>
              <span className="ta-table-meta">One-time {data.buckets?.oneTime ?? 0} · Returning {data.buckets?.returning ?? 0} · Sticky {data.buckets?.sticky ?? 0}</span>
            </div>
            <table className="ta-table">
              <thead>
                <tr>
                  <th className="ta-th">User</th>
                  <th className="ta-th">Teams</th>
                  <th className="ta-th">First seen</th>
                  <th className="ta-th">Last active</th>
                  <th className="ta-th ta-th-num">Active days</th>
                  <th className="ta-th ta-th-num">Weeks</th>
                  <th className="ta-th">Behavior</th>
                  <th className="ta-th ta-th-num">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data.users || []).slice().sort((a, b) => (b.activeDays - a.activeDays)).map((u) => {
                  const meta = CLASS_META[u.classification] || CLASS_META.inactive;
                  return (
                    <tr key={u.userId} className="ta-tr">
                      <td className="ta-td">
                        <div className="ta-user-info">
                          <div className="ta-avatar">{(u.name || u.email || "?")[0].toUpperCase()}</div>
                          <div>
                            <div className="ta-user-name">{u.name || "—"}</div>
                            <div className="ta-user-email">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="ta-td">
                        {(u.teamIds && u.teamIds.length)
                          ? <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{u.teamIds.map((tid) => <span key={tid} className="ta-badge ta-badge-role">{teamNameById(tid)}</span>)}</div>
                          : <span className="ta-badge ta-badge-default">—</span>}
                      </td>
                      <td className="ta-td" style={{ fontSize: 12, color: "#7a93bc" }}>{shortDate(u.firstActive)}</td>
                      <td className="ta-td" style={{ fontSize: 12, color: "#7a93bc" }}>{shortDate(u.lastActive)}</td>
                      <td className="ta-td ta-td-num">{u.activeDays}</td>
                      <td className="ta-td ta-td-num">{u.activeWeeks}</td>
                      <td className="ta-td"><span className={`ta-badge ${meta.cls}`}>{meta.label}</span></td>
                      <td className="ta-td ta-td-num">{u.actions}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Token Consumption tab ─────────────────────────────────────────────
function TokenConsumptionTab({ tenantId, token, from, to }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("");

  const [showUntracked, setShowUntracked] = useState(false);

  useEffect(() => {
    fetch(`${domain}/api/v1/tenant/teams`, { headers: { "x-access-token": token } })
      .then((r) => r.ok ? r.json() : [])
      .then((d) => setTeams(Array.isArray(d) ? d : (d.teams || [])))
      .catch(() => {});
  }, [token]);

  const fetchTokens = useCallback(() => {
    setLoading(true);
    setError(null);
    const qs = [`from=${from}`, `to=${to}`];
    if (teamId) qs.push(`teamId=${teamId}`);
    fetch(`${domain}/api/v1/tenant/tokens?${qs.join("&")}`, { headers: { "x-access-token": token } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => setData(d))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [from, to, teamId, token]);

  useEffect(() => { fetchTokens(); }, [fetchTokens]);

  const teamNameById = (id) => (teams.find((t) => t._id === id) || {}).name || id;
  const weeks = data?.weeks || [];
  const maxWeekTokens = Math.max(1, ...weeks.map((w) => w.tokens || 0));
  const maxWeekAvg = Math.max(1, ...weeks.map((w) => w.avgTokensPerTask || 0));

  return (
    <div>
      {/* Controls */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
        <label className="ta-date-label">Team / Project</label>
        <select className="ta-date-input" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
          <option value="">All teams</option>
          {teams.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
        </select>
      </div>

      {error && <div className="ta-error">⚠ {error}</div>}
      {loading && !data && <div className="ta-loading"><div className="ta-spinner" /><span>Fetching token usage…</span></div>}

      {data && (
        <>
          <div className="ta-cards">
            <SummaryCard icon="🔢" label="Total Tokens (I+O)" value={fmtTokens(data.totals?.tokens || 0)} accent="#4a90d9" />
            <SummaryCard icon="💰" label="Total Cost" value={fmtCost(data.totals?.cost)} accent="#2ebfa5" />
            <SummaryCard
              icon="✅"
              label={data.totals?.completedTasks != null ? `Completed Tasks / ${data.totals?.tasks || 0}` : "Tasks (sessions)"}
              value={data.totals?.completedTasks != null ? data.totals.completedTasks : (data.totals?.tasks || 0)}
              accent="#ffa032"
            />
            <SummaryCard
              icon="🎯"
              label="Success Rate"
              value={data.totals?.successRate != null ? `${data.totals.successRate}%` : "—"}
              accent="#9a75f6"
            />
            <SummaryCard icon="⚙️" label="Avg Tokens / Completed Task" value={fmtTokens(data.totals?.avgTokensPerTask || 0)} accent="#63b3ed" />
          </div>

          {/* Weekly tokens + efficiency */}
          <div className="ta-table-wrap" style={{ marginTop: 8 }}>
            <div className="ta-table-header">
              <span className="ta-table-title">Weekly Tokens &amp; Efficiency</span>
              <span className="ta-table-meta">{weeks.length} weeks · bar = tokens · ● = avg tokens/completed task (efficiency)</span>
            </div>
            {weeks.length === 0 ? (
              <div className="ta-empty">No token usage in this range.</div>
            ) : (
              <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
                {weeks.map((w) => (
                  <div key={w.weekStart} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 56, fontSize: 11, color: "#7a93bc", fontFamily: "monospace", flexShrink: 0 }}>{shortDate(w.weekStart)}</div>
                    <div style={{ position: "relative", flex: 1, height: 22, background: "rgba(255,255,255,0.05)", borderRadius: 6, overflow: "hidden" }}>
                      <div style={{ width: `${Math.round((w.tokens / maxWeekTokens) * 100)}%`, height: "100%", background: "#4a90d9" }} />
                      {/* efficiency marker: avg tokens/task, scaled to its own max */}
                      <div title={`Avg ${w.avgTokensPerTask.toLocaleString()} tokens/task`} style={{ position: "absolute", top: "50%", transform: "translate(-50%,-50%)", left: `${Math.round((w.avgTokensPerTask / maxWeekAvg) * 100)}%`, width: 8, height: 8, borderRadius: "50%", background: "#ffa032", border: "1px solid #0e1219" }} />
                    </div>
                    <div style={{ width: 210, fontSize: 11, color: "#dce8ff", textAlign: "right", flexShrink: 0 }}>
                      {fmtTokens(w.tokens)} tok · {(w.completedTasks || 0)}/{w.tasks} done · <span style={{ color: "#ffa032" }}>{fmtTokens(w.avgTokensPerTask)}/task</span> · <span style={{ color: "#2ebfa5" }}>{fmtCost(w.cost)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cost per team */}
          <div className="ta-table-wrap" style={{ marginTop: 16 }}>
            <div className="ta-table-header">
              <span className="ta-table-title">Cost per Team / Project</span>
              <span className="ta-table-meta">token spend per team over the selected range</span>
            </div>
            {(!data.teams || data.teams.length === 0) ? (
              <div className="ta-empty">No team usage yet — assign users to teams and generate activity.</div>
            ) : (
              <table className="ta-table">
                <thead>
                  <tr>
                    <th className="ta-th">Team / Project</th>
                    <th className="ta-th ta-th-num">Members</th>
                    <th className="ta-th ta-th-num">Tokens</th>
                    <th className="ta-th ta-th-cost">Cost</th>
                    <th className="ta-th ta-th-num">Tasks</th>
                    <th className="ta-th ta-th-num">Avg/Task</th>
                  </tr>
                </thead>
                <tbody>
                  {data.teams.map((t) => (
                    <tr key={t.teamId} className="ta-tr">
                      <td className="ta-td"><span className="ta-user-name">{t.name}</span></td>
                      <td className="ta-td ta-td-num">{t.members}</td>
                      <td className="ta-td ta-td-num">{fmtTokens(t.tokens)}</td>
                      <td className="ta-td ta-td-cost">{fmtCost(t.cost)}</td>
                      <td className="ta-td ta-td-num">{t.tasks}</td>
                      <td className="ta-td ta-td-num">{fmtTokens(t.avgTokensPerTask)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* By project / deliverable */}
          {(() => {
            const allProjects = data.projects || [];
            const tracked = allProjects.filter((p) => p.status !== "unknown");
            const untracked = allProjects.filter((p) => p.status === "unknown");
            const untrackedTokens = untracked.reduce((a, p) => a + (p.tokens || 0), 0);
            const untrackedCost = untracked.reduce((a, p) => a + (p.cost || 0), 0);
            const renderRow = (p) => {
              const tech = p.technology || "generation";
              const sMeta = {
                completed: { cls: "ta-badge-success", label: `${tech} generated`, title: `The ${tech} generation finished without error — this is a successful generation, not a deploy.` },
                failed:    { cls: "ta-badge-error",   label: `${tech} failed`,    title: `The ${tech} generation errored.` },
                running:   { cls: "ta-badge-warning", label: "generating…",        title: "A generation is in progress." },
              }[p.status] || { cls: "ta-badge-default", label: "untracked", title: "This session predates task tracking — no run outcome recorded." };
              return (
                <tr key={p.sessionId} className="ta-tr">
                  <td className="ta-td">
                    <span className="ta-user-name">{p.name}</span>
                    {p.savedProjectId && <span className="ta-badge ta-badge-success" style={{ marginLeft: 6, fontSize: 9 }}>saved</span>}
                  </td>
                  <td className="ta-td" style={{ textAlign: "center" }}>{p.technology ? <span className="ta-badge ta-badge-role">{p.technology}</span> : <span style={{ color: "#3d5273" }}>—</span>}</td>
                  <td className="ta-td" style={{ textAlign: "center" }}><span className={`ta-badge ${sMeta.cls}`} title={sMeta.title}>{sMeta.label}</span></td>
                  <td className="ta-td ta-td-num">{p.attempts != null ? p.attempts : "—"}</td>
                  <td className="ta-td ta-td-num">{fmtTokens(p.tokens)}</td>
                  <td className="ta-td ta-td-cost">{fmtCost(p.cost)}</td>
                </tr>
              );
            };
            return (
              <div className="ta-table-wrap" style={{ marginTop: 16 }}>
                <div className="ta-table-header">
                  <span className="ta-table-title">By Project / Deliverable</span>
                  <span className="ta-table-meta">{tracked.length} tracked · status = outcome of the latest generation run (per type), not a deploy</span>
                </div>
                {allProjects.length === 0 ? (
                  <div className="ta-empty">No project activity in this range.</div>
                ) : (
                  <table className="ta-table">
                    <thead>
                      <tr>
                        <th className="ta-th">Project</th>
                        <th className="ta-th" style={{ width: 120, textAlign: "center" }}>Type</th>
                        <th className="ta-th" style={{ width: 170, textAlign: "center" }}>Status</th>
                        <th className="ta-th ta-th-num" style={{ width: 90 }}>Attempts</th>
                        <th className="ta-th ta-th-num" style={{ width: 110 }}>Tokens</th>
                        <th className="ta-th ta-th-cost">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tracked.map(renderRow)}
                      {untracked.length > 0 && (
                        <tr>
                          <td className="ta-td" colSpan={6} style={{ background: "rgba(255,255,255,0.02)" }}>
                            <button
                              onClick={() => setShowUntracked((v) => !v)}
                              style={{ background: "none", border: "none", color: "#7a93bc", fontSize: 12, cursor: "pointer", fontFamily: "inherit", padding: "2px 0" }}
                            >
                              {showUntracked ? "▾" : "▸"} {untracked.length} older untracked session{untracked.length !== 1 ? "s" : ""} · {fmtTokens(untrackedTokens)} tok · {fmtCost(untrackedCost)}
                              <span style={{ color: "#3d5273" }}> (predate task tracking)</span>
                            </button>
                          </td>
                        </tr>
                      )}
                      {showUntracked && untracked.map(renderRow)}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })()}

          {/* Per-user breakdown */}
          <div className="ta-table-wrap" style={{ marginTop: 16 }}>
            <div className="ta-table-header">
              <span className="ta-table-title">Per-User Consumption</span>
              <span className="ta-table-meta">{(data.users || []).length} users</span>
            </div>
            <table className="ta-table">
              <thead>
                <tr>
                  <th className="ta-th">User</th>
                  <th className="ta-th">Teams</th>
                  <th className="ta-th ta-th-num">Tokens (I+O)</th>
                  <th className="ta-th ta-th-cost">Cost</th>
                  <th className="ta-th ta-th-num">Requests</th>
                  <th className="ta-th ta-th-num">Tasks</th>
                </tr>
              </thead>
              <tbody>
                {(data.users || []).map((u) => (
                  <tr key={u.userId} className="ta-tr">
                    <td className="ta-td">
                      <div className="ta-user-info">
                        <div className="ta-avatar">{(u.name || u.email || "?")[0].toUpperCase()}</div>
                        <div>
                          <div className="ta-user-name">{u.name || "—"}</div>
                          <div className="ta-user-email">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="ta-td">
                      {(u.teamIds && u.teamIds.length)
                        ? <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{u.teamIds.map((tid) => <span key={tid} className="ta-badge ta-badge-role">{teamNameById(tid)}</span>)}</div>
                        : <span className="ta-badge ta-badge-default">—</span>}
                    </td>
                    <td className="ta-td ta-td-num">{fmtTokens(u.tokens)}</td>
                    <td className="ta-td ta-td-cost">{fmtCost(u.cost)}</td>
                    <td className="ta-td ta-td-num">{u.requests}</td>
                    <td className="ta-td ta-td-num">{u.tasks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────
function TenantAdmin() {
  const [activeTab, setActiveTab] = useState("usage");
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState("totalEstimatedCostUSD");
  const [sortDir, setSortDir] = useState("desc");

  const storedUser = getStoredUser();
  const tenantId = storedUser.tenantId || "";
  const token = localStorage.getItem("token") || "";

  const fetchData = useCallback(() => {
    if (USE_MOCK) { setData(MOCK_DATA); return; }
    setLoading(true);
    setError(null);
    const fetchUrl = `${domain}/api/v1/tenant/usage?from=${from}&to=${to}&tenantId=${tenantId}`;
    console.log("[TenantAdmin] REQUEST →", fetchUrl);
    fetch(fetchUrl, { headers: { "x-access-token": token } })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => {
        console.log("[TenantAdmin] RESPONSE ←", d);
        setData({
          users: Array.isArray(d.users) ? d.users : Array.isArray(d) ? d : [],
          summary: d.summary || { totalCost: 0, totalRequests: 0, totalUsers: 0, totalInputTokens: 0, totalOutputTokens: 0, totalCacheReadTokens: 0 },
        });
      })
      .catch((e) => { console.error("[TenantAdmin] ERROR", e); setError(e.message); })
      .finally(() => setLoading(false));
  }, [from, to, tenantId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const sortedUsers = data
    ? [...data.users]
        .map((u) => ({ ...u, _totalCost: data.summary?.totalCost || 1 }))
        .sort((a, b) => {
          const v = sortDir === "asc" ? 1 : -1;
          if (sortKey === "name") return v * (a.name || "").localeCompare(b.name || "");
          return v * ((b[sortKey] || 0) - (a[sortKey] || 0));
        })
    : [];

  const SortIcon = ({ k }) =>
    sortKey === k
      ? <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.8 }}>{sortDir === "asc" ? "▲" : "▼"}</span>
      : <span style={{ fontSize: 10, marginLeft: 4, opacity: 0.25 }}>⇅</span>;

  const TABS = [
    { key: "usage",       label: "Cost Dashboard" },
    { key: "adoption",    label: "Adoption" },
    { key: "consumption", label: "Token Consumption" },
    { key: "teams",       label: "Teams / Projects" },
    { key: "users",       label: "User Approvals" },
    { key: "tokens",      label: "Token Extensions" },
  ];

  return (
    <div className="root">
      <CustomHeader />
      <main className="content">
        <div className="ta-page">

          {/* ── Page header ── */}
          <div className="ta-header">
            <div>
              <div className="ta-header-badge">Tenant Admin</div>
              <h1 className="ta-header-title">Admin Dashboard</h1>
              <p className="ta-header-sub">Manage your tenant's users, permissions and AI token usage</p>
            </div>
            {(activeTab === "usage" || activeTab === "adoption" || activeTab === "consumption") && (
              <div className="ta-date-row" style={{ flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {RANGE_PRESETS.map((p) => {
                    const r = p.range();
                    const active = from === r.from && to === r.to;
                    return (
                      <button
                        key={p.key}
                        className={`ta-tab${active ? " ta-tab-active" : ""}`}
                        style={{ padding: "4px 10px", fontSize: 12, borderRadius: 6 }}
                        onClick={() => { setFrom(r.from); setTo(r.to); }}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
                <label className="ta-date-label">From</label>
                <input type="date" className="ta-date-input" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
                <label className="ta-date-label">To</label>
                <input type="date" className="ta-date-input" value={to} min={from} max={today()} onChange={(e) => setTo(e.target.value)} />
                <button className="ta-apply-btn" onClick={fetchData} disabled={loading}>
                  {loading ? "Loading…" : "Apply"}
                </button>
              </div>
            )}
          </div>

          {/* ── Tabs ── */}
          <div className="ta-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`ta-tab${activeTab === t.key ? " ta-tab-active" : ""}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* ── Cost Dashboard tab ── */}
          {activeTab === "usage" && (
            <>
              {error && <div className="ta-error">⚠ {error}</div>}

              {data && (
                <div className="ta-cards">
                  <SummaryCard icon="💰" label="Total Est. Cost"  value={fmtCost(data.summary?.totalCost)}               accent="#2ebfa5" />
                  <SummaryCard icon="👥" label="Active Users"     value={data.summary?.totalUsers ?? data.users.length}  accent="#4a90d9" />
                  <SummaryCard icon="📡" label="Total Requests"   value={data.summary?.totalRequests}                    accent="#9a75f6" />
                  <SummaryCard icon="📥" label="Input Tokens"     value={fmtTokens(data.summary?.totalInputTokens)}      accent="#ffa032" />
                  <SummaryCard icon="📤" label="Output Tokens"    value={fmtTokens(data.summary?.totalOutputTokens)}     accent="#63b3ed" />
                </div>
              )}

              {loading && !data && (
                <div className="ta-loading"><div className="ta-spinner" /><span>Fetching usage data…</span></div>
              )}
              {data && data.users.length === 0 && (
                <div className="ta-empty">No usage data found for the selected date range.</div>
              )}
              {data && data.users.length > 0 && (
                <div className="ta-table-wrap">
                  <div className="ta-table-header">
                    <span className="ta-table-title">User Breakdown</span>
                    <span className="ta-table-meta">{data.users.length} users · {from} → {to}</span>
                  </div>
                  <table className="ta-table">
                    <thead>
                      <tr>
                        <th className="ta-th ta-th-rank">#</th>
                        <th className="ta-th ta-th-sort" onClick={() => handleSort("name")}>User <SortIcon k="name" /></th>
                        <th className="ta-th ta-th-sort ta-th-num" onClick={() => handleSort("totalRequestCount")}>Requests <SortIcon k="totalRequestCount" /></th>
                        <th className="ta-th ta-th-sort ta-th-num" onClick={() => handleSort("totalInputTokens")}>Tokens (I+O) <SortIcon k="totalInputTokens" /></th>
                        <th className="ta-th ta-th-num">Cache Read</th>
                        <th className="ta-th ta-th-sort ta-th-cost" onClick={() => handleSort("totalEstimatedCostUSD")}>Est. Cost <SortIcon k="totalEstimatedCostUSD" /></th>
                        <th className="ta-th" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedUsers.map((user, i) => (
                        <UserRow key={user.userId || user.email} user={user} rank={i + 1} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ── Adoption tab ── */}
          {activeTab === "adoption" && (
            <AdoptionTab tenantId={tenantId} token={token} from={from} to={to} />
          )}

          {/* ── Token Consumption tab ── */}
          {activeTab === "consumption" && (
            <TokenConsumptionTab tenantId={tenantId} token={token} from={from} to={to} />
          )}

          {/* ── Teams / Projects tab ── */}
          {activeTab === "teams" && (
            <TeamsTab tenantId={tenantId} token={token} />
          )}

          {/* ── User Approvals tab ── */}
          {activeTab === "users" && (
            <UserApprovalsTab tenantId={tenantId} token={token} />
          )}

          {/* ── Token Extensions tab ── */}
          {activeTab === "tokens" && (
            <TokenExtensionsTab tenantId={tenantId} token={token} />
          )}

        </div>
      </main>
    </div>
  );
}

export default TenantAdmin;
